import React, { useState, useMemo } from 'react';
import { Trash2, Search, Filter, Mail, Clock, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const EnquiriesTab = ({ enquiries, onStatusChange, onDelete }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Get unique enquiry types
  const enquiryTypes = useMemo(() => {
    const types = [...new Set(enquiries.map(e => e.enquiry_type).filter(Boolean))];
    return types.length > 0 ? types : ['general', 'package', 'fleet'];
  }, [enquiries]);

  // Filter and sort enquiries
  const filteredEnquiries = useMemo(() => {
    let result = [...enquiries];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(e => 
        e.name?.toLowerCase().includes(query) ||
        e.email?.toLowerCase().includes(query) ||
        e.phone?.includes(query) ||
        e.message?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(e => e.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(e => e.enquiry_type === typeFilter);
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return 0;
    });

    return result;
  }, [enquiries, searchQuery, statusFilter, typeFilter, sortBy]);

  // Summary stats
  const stats = useMemo(() => ({
    total: enquiries.length,
    pending: enquiries.filter(e => e.status === 'pending').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    resolved: enquiries.filter(e => e.status === 'resolved').length,
  }), [enquiries]);

  return (
    <div data-testid="enquiries-tab" className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-s-bg2 border-s-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-s-text2 text-xs">Total Enquiries</p>
                <p className="text-2xl font-bold text-s-text">{stats.total}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-s-primary/15 flex items-center justify-center">
                <Mail className="w-5 h-5 text-s-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-s-bg2 border-s-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-s-text2 text-xs">Pending</p>
                <p className="text-2xl font-bold text-s-primary">{stats.pending}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-s-primary/15 flex items-center justify-center">
                <Clock className="w-5 h-5 text-s-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-s-bg2 border-s-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-s-text2 text-xs">Contacted</p>
                <p className="text-2xl font-bold text-s-primary">{stats.contacted}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-s-primary/15 flex items-center justify-center">
                <Phone className="w-5 h-5 text-s-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-s-bg2 border-s-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-s-text2 text-xs">Resolved</p>
                <p className="text-2xl font-bold text-s-primary">{stats.resolved}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-s-primary/15 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-s-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-s-bg2 border-s-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-s-muted" />
              <Input
                placeholder="Search by name, email, phone, or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-s-card border-s-border pl-10"
                data-testid="enquiry-search"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 bg-s-card border-s-border" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-s-card border-s-border">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-32 bg-s-card border-s-border" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-s-card border-s-border">
                  <SelectItem value="all">All Types</SelectItem>
                  {enquiryTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-32 bg-s-card border-s-border" data-testid="sort-filter">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent className="bg-s-card border-s-border">
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="name">By Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-s-text2 text-sm">
          Showing {filteredEnquiries.length} of {enquiries.length} enquiries
          {(searchQuery || statusFilter !== 'all' || typeFilter !== 'all') && (
            <Button 
              variant="link" 
              className="text-s-primary p-0 h-auto ml-2"
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); }}
            >
              Clear filters
            </Button>
          )}
        </p>
      </div>

      {/* Enquiries List */}
      <div className="space-y-3">
        {filteredEnquiries.length === 0 ? (
          <Card className="bg-s-bg2 border-s-border">
            <CardContent className="p-8 text-center">
              <Filter className="w-12 h-12 text-s-muted mx-auto mb-3" />
              <p className="text-s-text2">No enquiries match your filters</p>
              <Button 
                variant="outline" 
                className="mt-3 border-s-border text-s-text2"
                onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); }}
              >
                Clear filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredEnquiries.map((enq) => (
            <Card key={enq.id} className="bg-s-bg2 border-s-border hover:border-s-primary/30 transition-colors" data-testid={`enquiry-card-${enq.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-s-text font-semibold">{enq.name}</h3>
                      <Badge className={
                        enq.status === 'pending' ? 'bg-s-primary/20 text-s-primary' :
                        enq.status === 'contacted' ? 'bg-s-primary/20 text-s-primary' :
                        'bg-s-primary/20 text-s-primary'
                      }>
                        {enq.status}
                      </Badge>
                      {enq.enquiry_type && (
                        <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30" variant="outline">
                          {enq.enquiry_type}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-s-text2 text-sm mb-2">
                      <a href={`mailto:${enq.email}`} className="hover:text-s-primary transition-colors">{enq.email}</a>
                      <span>&bull;</span>
                      <a href={`tel:${enq.phone}`} className="hover:text-s-primary transition-colors">{enq.phone}</a>
                    </div>
                    <p className="text-s-text2 text-sm line-clamp-2">{enq.message}</p>
                    <p className="text-s-muted text-xs mt-2">
                      {new Date(enq.created_at).toLocaleString('en-IN', { 
                        day: 'numeric', month: 'short', year: 'numeric', 
                        hour: '2-digit', minute: '2-digit' 
                      })}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Select value={enq.status} onValueChange={(value) => onStatusChange(enq.id, value)}>
                      <SelectTrigger className="w-28 bg-s-card border-s-border text-s-text text-xs h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-s-card border-s-border">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => onDelete('enquiry', enq.id)} 
                      className="border-red-500/50 text-s-primary hover:bg-s-primary/20 h-8"
                      data-testid={`delete-enquiry-${enq.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default EnquiriesTab;
